/**
 * One-click actions for an optimization run.
 *
 * The agent's opening message summarises the run well — "28 suggestions, 17
 * high priority, the two biggest levers are…" — and then leaves the user with
 * only a text box. Acting on that summary meant typing a sentence and hoping
 * the agent parsed it the way you meant. This is the missing half: the same
 * asks, as buttons, carrying the counts the summary just quoted.
 *
 * IT PROPOSES; IT DOES NOT APPLY. Every button sends a message to the agent,
 * which comes back with a preview rendered by BulkEditPanel — the same
 * propose → disclose → Confirm card the bulk editor uses. So the confirm gate
 * is untouched: nothing here can reach the live campaign without the user
 * reading a diff and pressing Confirm.
 *
 * THE LABELS SAY "PREVIEW" BECAUSE THAT IS WHAT THE BUTTON DOES. They used to
 * say "Apply all 16", defended by the note underneath explaining that they only
 * ask for a preview — i.e. the button said one thing and the fine print said
 * the opposite, on a surface whose entire job is making a live-campaign change
 * feel safe. A user pressing "Apply all 16" believes 16 changes just went out.
 * The button now names the step it actually performs; the word Apply belongs to
 * the Confirm inside the preview card, which is the only thing that applies.
 * The message SENT to the agent still says "Apply …" — that is the intent being
 * described to the model, and it already demands a preview first.
 *
 * The buttons are built from the DIGEST, not from parsed prose, so a count on a
 * button is the count the run actually has. A bucket with nothing in it is not
 * rendered at all rather than rendered as "Preview 0".
 *
 * APPLIED WORK STAYS ON SCREEN, GREYED. Applied suggestions used to be filtered
 * out silently: press "Apply all 6", confirm, and the button became "Apply all
 * 4" — or the bar vanished — with nothing anywhere saying the other two had
 * landed. A bucket with nothing left to do now renders as a disabled "N high
 * applied ✓" instead of disappearing, so the run reads as progress rather than
 * as buttons that keep shrinking on their own.
 */

import { useState } from 'react';

const PRIORITY_ORDER = ['high', 'medium', 'low'];

/**
 * Suggestions the user can still act on: applicable, and not already applied.
 * Mirrors digest.counts.applicable so a button never offers work that is done.
 */
const pending = (digest) => (digest?.suggestions || [])
  .filter((s) => s.applicable && !s.alreadyApplied);

/** Suggestions already applied — kept so the bar can show them done, greyed. */
const applied = (digest) => (digest?.suggestions || []).filter((s) => s.alreadyApplied);

export function OptimizationActionBar({ digest, onAsk, disabled = false, busy = false }) {
  // Which button was pressed, so the working state attaches to the action the
  // user took rather than floating under the row as a bare "Working…".
  const [pendingAsk, setPendingAsk] = useState(null);
  const rows = pending(digest);
  const done = applied(digest);
  // Nothing to offer AND nothing to report — a run with no actionable
  // suggestions at all. (Having something applied is enough to render: that is
  // the completed state, not an empty one.)
  if (!rows.length && !done.length) return null;

  // Every priority the run touches, whether its work is outstanding or done, so
  // a fully-applied bucket keeps its place in the row instead of vanishing.
  const byPriority = PRIORITY_ORDER
    .map((p) => ({
      priority: p,
      count: rows.filter((s) => s.priority === p).length,
      appliedCount: done.filter((s) => s.priority === p).length,
    }))
    .filter((b) => b.count > 0 || b.appliedCount > 0);

  // A single-priority run doesn't need both "Preview all 12" and "Preview 12
  // high" — they are the same button twice. Offer the split only when it splits.
  const showSplit = byPriority.length > 1;

  const act = (key, text) => {
    if (disabled || busy) return;
    setPendingAsk(key);
    onAsk?.(text);
  };
  // NOT cleared when busy goes false. Clearing during render raced the parent:
  // the click set the marker, this component re-rendered before `busy` had
  // flipped, and the marker was wiped before it was ever read. Overwriting on
  // each click is enough — the marker is only ever READ while busy, so it always
  // describes the most recent action.
  //
  // `null` is reachable when the turn was started by something else (a typed
  // message), so its copy is the generic one — and, like the other two, it still
  // says nothing was sent.
  const busyText = pendingAsk === 'rank'
    ? 'Reading the run — nothing is being applied.'
    : pendingAsk
      ? 'Building your preview — nothing has been sent to the campaign.'
      : 'Working — nothing has been sent to the campaign.';

  return (
    <div className="clw-opt-actions">
      <div className="clw-opt-actions-lbl">Act on this run</div>
      <div className="clw-opt-actions-row">
        {rows.length > 0 ? (
          <button
            type="button"
            className="clw-opt-action primary"
            disabled={disabled || busy}
            onClick={() => act('all',
              `Apply all ${rows.length} applicable suggestions. Show me the full preview before anything is sent.`,
            )}
            title="Ask the agent to propose every applicable suggestion — you still confirm before anything reaches the campaign"
          >
            Preview all {rows.length}
          </button>
        ) : (
          <button
            type="button"
            className="clw-opt-action applied"
            disabled
            title="Every applicable suggestion in this run has been applied"
          >
            ✓ All {done.length} applied
          </button>
        )}

        {showSplit && byPriority.map(({ priority, count, appliedCount }) => (
          count > 0 ? (
            <button
              key={priority}
              type="button"
              className="clw-opt-action"
              disabled={disabled || busy}
              onClick={() => act(priority,
                `Apply the ${count} ${priority}-priority suggestions. Show me the preview first.`,
              )}
              title={appliedCount
                ? `Propose the ${count} ${priority}-priority suggestions still outstanding (${appliedCount} already applied)`
                : `Propose only the ${priority}-priority suggestions`}
            >
              Preview {count} {priority}
            </button>
          ) : (
            <button
              key={priority}
              type="button"
              className="clw-opt-action applied"
              disabled
              title={`All ${appliedCount} ${priority}-priority suggestions have been applied`}
            >
              ✓ {appliedCount} {priority} applied
            </button>
          )
        ))}

        <button
          type="button"
          className="clw-opt-action ghost"
          disabled={disabled || busy}
          onClick={() => act('rank', 'Which of these would move the needle most, and why? Do not propose anything yet.')}
          title="Ask for the ranking before deciding"
        >
          What matters most?
        </button>
      </div>
      {/* THE SAFETY LINE IS PERMANENT.
          It used to be REPLACED by a bare "Working…" while the agent thought —
          so at the one moment the user is most anxious, having just pressed
          "Apply all 6" on a live campaign, the only sentence telling them
          nothing was being applied disappeared and was replaced by a word that
          reads like it is applying. That is the opposite of what a progress
          state is for. The reassurance now stays put and the progress line is
          ADDED above it, saying explicitly that nothing has been sent. */}
      {busy && (
        <div className="clw-opt-actions-busy" role="status">
          <span className="clw-opt-actions-spinner" aria-hidden="true" />
          {busyText}
        </div>
      )}
      <div className="clw-opt-actions-note">
        These buttons ask for a preview. <strong>Nothing reaches the campaign
        until you read the change and press Confirm.</strong>
      </div>
    </div>
  );
}

export default OptimizationActionBar;
